import { Modal, Button } from "react-bootstrap";

interface ConfirmSubscriptionModalProps {
  show: boolean;
  message: string;
  title?: string;
  badge?: string;
  perks?: string[];
  buttonText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const defaultPerks = [
  "Direct matching with verified global network",
  "Full access to AI intelligence & recommendation tools",
  "Priority support and streamlined expansion",
];

const CheckCircleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle cx="8" cy="8" r="7" fill="#66C15E" />
    <path
      d="M5 8l2.2 2.2L11 5.5"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SparkleBoltIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="rgba(255,255,255,0.25)"
    />
  </svg>
);

const ConfirmSubscriptionModal = ({
  show,
  message,
  title = "Unlock Platform Access",
  badge = "Plan Upgrade",
  perks = defaultPerks,
  buttonText = "Explore Plans & Upgrade",
  onConfirm,
  onCancel,
}: ConfirmSubscriptionModalProps) => {
  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      contentClassName="border-0 shadow-lg"
      dialogClassName="smart-confirm-modal"
      style={{ zIndex: 1060 }}
    >
      <div
        style={{
          borderRadius: 20,
          overflow: "hidden",
          background: "#ffffff",
          fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Accent gradient bar */}
        <div
          style={{
            height: 4,
            background: "linear-gradient(90deg, #66C15E 0%, #34d399 50%, #10b981 100%)",
          }}
        />

        {/* Header with soft glow backdrop */}
        <div
          style={{
            background: "linear-gradient(180deg, #f3faf2 0%, #ffffff 100%)",
            padding: "2rem 1.75rem 1.25rem",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Close button */}
          <button
            onClick={onCancel}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Icon with glowing shadow */}
          <div
            style={{
              width: 58,
              height: 58,
              background: "linear-gradient(135deg, #66C15E 0%, #43a83b 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 0.85rem",
              boxShadow: "0 10px 24px -4px rgba(102, 193, 94, 0.45)",
            }}
          >
            <SparkleBoltIcon />
          </div>

          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#edf7ec",
              color: "#276722",
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 12px",
              borderRadius: 20,
              marginBottom: 8,
              letterSpacing: "0.03em",
              border: "1px solid #d4eed2",
              textTransform: "uppercase",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#66C15E",
                boxShadow: "0 0 0 2px rgba(102, 193, 94, 0.25)",
              }}
            />
            {badge}
          </span>

          <h4
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0f172a",
              margin: "4px 0 0",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </h4>
        </div>

        <Modal.Body style={{ padding: "0.5rem 1.75rem 1.75rem" }}>
          {/* Card Message Callout */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderLeft: "4px solid #66C15E",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: "1.25rem",
            }}
          >
            <p
              style={{
                fontSize: 14,
                color: "#334155",
                margin: 0,
                lineHeight: 1.55,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {message}
            </p>
          </div>

          {/* Perks list */}
          <div
            style={{
              background: "#fdfdfd",
              borderRadius: 12,
              padding: "0.5rem 0.25rem",
              marginBottom: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {perks.map((perk) => (
              <div
                key={perk}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 13,
                  color: "#475569",
                  fontWeight: 500,
                }}
              >
                <CheckCircleIcon />
                <span>{perk}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <Button
            onClick={onConfirm}
            style={{
              width: "100%",
              height: 44,
              marginBottom: 10,
              background: "linear-gradient(135deg, #66C15E 0%, #46aa3e 100%)",
              boxShadow: "0 4px 14px rgba(102, 193, 94, 0.35)",
              border: "none",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 14.5,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "all 0.2s ease",
            }}
          >
            {buttonText}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <Button
            variant="outline-secondary"
            onClick={onCancel}
            style={{
              width: "100%",
              height: 42,
              fontSize: 13.5,
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#e2e8f0",
              background: "#ffffff",
              borderRadius: 10,
              transition: "all 0.15s ease",
            }}
          >
            Maybe later
          </Button>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default ConfirmSubscriptionModal;
